import {
  Box,
  Container,
  Typography,
  useTheme,
  Button,
  Grid,
  Divider,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { ParallaxSection } from "../components/ParallaxSection";
import { SkillCard } from "../components/SkillCard";
import { fadeIn, staggeredContainer } from "../utils/animations";
import { skills } from "../data/skills";
import { ProjectsSection } from "../components/ProjectsSection";
import { TestimonialsSection } from "../components/TestimonialsSection";
import { ResumeSection } from "../components/ResumeSection";
import { BlogSection } from "../components/BlogSection";
import { ContactForm } from "../components/ContactForm";
import { useState } from "react";
import EmailIcon from "@mui/icons-material/Email";
import felix from "../assets/media/felix.png";

const Home = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <ParallaxSection>
        <Container
          maxWidth="lg"
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <Box maxWidth="800px">
            <motion.div
              variants={fadeIn}
              initial="hidden"
              animate="visible"
              viewport={{ margin: "-100px" }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.2rem", sm: "3.5rem", md: "4.5rem" },
                  fontWeight: 700,
                  mb: 1,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  textIndent: "-1.5px",
                }}
              >
                Christopher Perault
              </Typography>
              <Typography
                variant="h4"
                color="secondary"
                gutterBottom
                sx={{ fontSize: { xs: "1.2rem", sm: "2rem" } }}
              >
                Software Engineer
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  maxWidth: "600px",
                  fontSize: { xs: "0.7rem", sm: "1.25rem" },
                  textIndent: "1px",
                }}
              >
                Transforming ideas into systems; creating purpose in chaos
              </Typography>
            </motion.div>
          </Box>
        </Container>
      </ParallaxSection>

      {/* About Section */}
      <Box sx={{ py: 8, backgroundColor: "background.paper" }}>
        <Container maxWidth="lg">
          <motion.div
            variants={staggeredContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: "-100px" }}
          >
            <motion.div variants={fadeIn}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  mb: 6,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 600,
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                Let's Get Personal
              </Typography>
            </motion.div>

            {/* Responsive Flex Container */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: { xs: 4, md: 6 },
              }}
            >
              {/* Main About Section */}
              <motion.div variants={fadeIn} style={{ flex: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: { xs: "justify", md: "justify" },
                  }}
                >
                  <Typography variant="body2" gutterBottom>
                    I was born and raised in the flat, gritty sprawl of Omaha,
                    Nebraska—a place where the wind howls, the air bites in
                    winter, and resilience flows through its pothole-filled veins. Music was my
                    first love; from sixth grade through university, I lived and
                    breathed the rhythms of a band geek's world. Life, in its
                    unpredictable way, had other plans.
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    After stepping away from university, I pursued experience in
                    customer service and IT—work that kept me moving but never
                    fully scratched the career itch. In the summer of 2017, I
                    rekindled the spark I first felt in my two high school Java
                    classes. I enrolled in a specialized two-year career
                    program, immersing myself in the craft of computer and web
                    programming, where logic met creativity and everything
                    clicked.
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Since then, I've lost both parents and left the place I once
                    called home. I traveled west, finding solace in the
                    breathtaking mountains of Colorado before finally making my
                    way to the lush beauty of Western Washington where I now reside. I've
                    built my career at the intersection of structure and
                    spontaneity—starting in small startups, where constraints
                    fuel scrappy ingenuity, and then at Nordstrom, where, for
                    nearly three years, I grew across three separate teams,
                    working on projects at scale. Now, I'm a Senior Software Engineer
                    at Classmates, one of the oldest social networks, where I have the
                    privilege of building on the technology connecting millions of people
                    through shared memories and nostalgia from the most formative years
                    of their lives.
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    I thrive in the space between complexity and clarity, where
                    code isn't just syntax but a means of transformation.
                    Whether maintaining or modernizing legacy systems, building
                    developer tooling, or creating intentional new experiences,
                    my goal is simple: build things that work, endure, and bring
                    purpose to the chaos.
                  </Typography>
                </Box>
              </motion.div>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", md: "block" } }}
              />

              {/* "Outside of work" Section */}
              <motion.div variants={fadeIn} style={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "column" },
                    alignItems: "center",
                    textAlign: { xs: "justify", md: "justify" },
                    gap: { xs: 4, md: 2 },
                  }}
                >
                  <img
                    src={felix}
                    alt="Félix, the cat"
                    draggable="false"
                    style={{ width: "200px", height: "400px" }}
                  />
                  <Typography variant="body2">
                    When I'm not working, you'll find me exploring, discovering
                    new food, soaking in the energy of live music and night
                    life, diving into new technologies and open-source projects,
                    or sharing quiet moments at home with my son, Félix, as we
                    snuggle up together on the couch. I do my best to live
                    intentionally and I'm always on the lookout for new
                    experiences that can help me grow.
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Skills Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <motion.div
            variants={staggeredContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                mb: 6,
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 600,
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              Skills & Technologies
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gridTemplateRows: "repeat(2, 1fr)",
                gap: 2,
                "@media (max-width: 1200px)": {
                  gridTemplateColumns: "repeat(2, 1fr)",
                },
                "@media (max-width: 600px)": {
                  gridTemplateColumns: "1fr",
                },
              }}
            >
              {skills.map((skill, index) => (
                <Box key={skill.name}>
                  <SkillCard {...skill} index={index} />
                </Box>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Resume Section */}
      <ResumeSection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Projects Section */}
      <ProjectsSection />

      {/* Blog Section */}
      <BlogSection />

      {/* Floating Contact Button */}
      <Box
        component={motion.div}
        whileTap={{ scale: 0.95 }}
        sx={{
          position: "fixed",
          bottom: { xs: 16, md: 32 },
          right: { xs: 16, md: 32 },
          zIndex: 1000,
        }}
      >
        <motion.div
          layout
          style={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 28,
            boxShadow: theme.shadows[4],
            overflow: "hidden",
            display: "flex",
            width: "auto",
          }}
        >
          <Button
            variant="contained"
            onClick={() => setContactOpen(true)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              minWidth: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: isHovered ? "0 20px" : "0",
              transition: "padding 0.2s ease-out",
              "&:hover": {
                backgroundColor: "background.paper",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <EmailIcon sx={{ color: "secondary.main" }} />
              <AnimatePresence mode="wait">
                {isHovered && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "auto", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{
                      duration: 0.15,
                      ease: [0.0, 0.0, 0.2, 1],
                    }}
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      variant="button"
                      sx={{
                        color: "secondary.main",
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      Let's Talk
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Button>
        </motion.div>
      </Box>

      {/* Contact Form Dialog */}
      <ContactForm open={contactOpen} onClose={() => setContactOpen(false)} />
    </Box>
  );
};

export default Home;
